import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Package, Mail, User, ArrowRight, Loader2, ExternalLink, Tag, CheckCircle2, PawPrint, Ticket } from 'lucide-react';
import { useBox } from '../context/BoxContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { DogPicker } from './DogPicker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CheckoutModal = ({ open, onClose }) => {
  const { selectedProducts, boxSize, getCurrentBoxInfo, clearBox } = useBox();
  const { customer } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [dogName, setDogName] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [promoResult, setPromoResult] = useState(null); // { valid, redirect_url, source, label }
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [vouchers, setVouchers] = useState([]);

  const boxInfo = getCurrentBoxInfo();

  // Prefill name/email + fetch vouchers when a logged-in customer opens the
  // modal so their wheel prizes surface as tap-to-apply chips.
  useEffect(() => {
    if (!open || !customer) return;
    if (customer.name) setName(prev => prev || customer.name);
    if (customer.email) setEmail(prev => prev || customer.email);
    const token = localStorage.getItem('customer_token');
    if (!token) return;
    axios
      .get(`${API}/customer/vouchers`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setVouchers((res.data || []).filter(v => !v.used_at && v.code)))
      .catch(() => setVouchers([]));
  }, [open, customer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim() || !email.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      // If discount code entered, validate it
      let validatedPromo = promoResult;
      if (discountCode.trim() && !validatedPromo?.valid) {
        const promoRes = await axios.post(`${API}/validate-promo-code`, {
          code: discountCode.trim()
        });
        validatedPromo = promoRes.data;
        setPromoResult(validatedPromo);
        
        if (!validatedPromo.valid) {
          toast.error('Invalid discount code');
          setIsSubmitting(false);
          return;
        }
      }

      // Create order in backend
      await axios.post(`${API}/orders`, {
        customer_name: name.trim(),
        customer_email: email.trim(),
        dog_name: dogName.trim() || null,
        box_size: boxSize,
        selected_products: selectedProducts.map((p) => p.id),
        promo_code: discountCode.trim() || null,
      });

      // Determine redirect URL
      if (validatedPromo?.valid && validatedPromo?.redirect_url) {
        setRedirectUrl(validatedPromo.redirect_url);
        toast.success('Discount applied! Your box is free.');
        setIsComplete(true);
      } else {
        // No promo — auto-redirect to Shopify immediately
        toast.success('Order saved! Redirecting to checkout...');
        const shopifyLink = boxInfo.shopify_link;
        // Clear and close first
        clearBox();
        setName('');
        setEmail('');
        setDiscountCode('');
        setPromoResult(null);
        setIsComplete(false);
        setRedirectUrl(null);
        onClose();
        // Navigate to Shopify checkout in the same tab
        if (shopifyLink) {
          window.location.href = shopifyLink;
        }
      }
      
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRedirect = () => {
    const url = redirectUrl;
    
    // Clear everything
    clearBox();
    setName('');
    setEmail('');
    setDiscountCode('');
    setPromoResult(null);
    setIsComplete(false);
    setRedirectUrl(null);
    onClose();

    // Navigate to the promo redirect URL
    if (url) {
      window.location.href = url;
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setName('');
      setEmail('');
      setDiscountCode('');
      setPromoResult(null);
      setIsComplete(false);
      setRedirectUrl(null);
      onClose();
    }
  };

  const handleApplyCode = async (codeOverride) => {
    const code = (codeOverride ?? discountCode).trim();
    if (!code) return;
    try {
      const res = await axios.post(`${API}/validate-promo-code`, {
        code,
        email: email.trim() || undefined,
      });
      setPromoResult(res.data);
      if (codeOverride) setDiscountCode(code);
      if (res.data.valid) {
        if (res.data.source === 'voucher') {
          toast.success(`${res.data.label || 'Voucher'} applied — used at checkout.`);
        } else {
          toast.success('Discount code applied! Box is free.');
        }
      } else {
        toast.error('Invalid discount code');
      }
    } catch {
      toast.error('Could not validate code');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="checkout-modal">
        {!isComplete ? (
          <>
            <DialogHeader>
              <div className="w-14 h-14 rounded-xl bg-[#0FA49C]/10 flex items-center justify-center mb-2">
                <Package className="w-7 h-7 text-[#0FA49C]" />
              </div>
              <DialogTitle className="text-2xl font-bold text-[#222]">
                Complete Your Box
              </DialogTitle>
              <DialogDescription className="text-[#666]">
                Enter your details to proceed to checkout
              </DialogDescription>
            </DialogHeader>

            {/* Order Summary */}
            <div className="bg-gray-50 rounded-xl p-4 my-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[#222] font-medium">{boxInfo.name}</span>
                <span className={`font-bold ${promoResult?.valid ? 'line-through text-gray-400' : 'text-[#0FA49C]'}`}>
                  £{boxInfo.price.toFixed(2)}
                </span>
              </div>
              {promoResult?.valid && (
                <div className="flex justify-between items-center mb-2">
                  <span className="text-green-700 font-medium flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" />
                    Discount Applied
                  </span>
                  <span className="font-bold text-green-700">FREE</span>
                </div>
              )}
              <p className="text-sm text-[#666]">
                {selectedProducts.length} samples: {selectedProducts.map((p) => p.name).join(', ')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#222] font-medium">
                  Your Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 bg-gray-50 border-gray-200 focus:border-[#0FA49C]"
                    required
                    data-testid="checkout-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#222] font-medium">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-gray-50 border-gray-200 focus:border-[#0FA49C]"
                    required
                    data-testid="checkout-email-input"
                  />
                </div>
              </div>

              <DogPicker
                value={dogName}
                onChange={setDogName}
                label={<>Dog's Name <span className="text-[#999] font-normal">(optional)</span></>}
                placeholder="Enter your dog's name"
                testId="checkout-dog-name"
              />

              {/* Discount Code */}
              <div className="space-y-2">
                <Label htmlFor="discount-code" className="text-[#222] font-medium">
                  Discount Code <span className="text-[#999] font-normal">(optional)</span>
                </Label>

                {vouchers.length > 0 && (
                  <div className="rounded-lg bg-[#f5fbfa] border border-[#0FA49C]/30 p-2.5" data-testid="checkout-your-vouchers">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#0FA49C] flex items-center gap-1.5 mb-1.5">
                      <Ticket className="w-3 h-3" /> Your vouchers · tap to apply
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {vouchers.map(v => (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => handleApplyCode(v.code)}
                          className="inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-2.5 py-1 bg-white text-[#1f6d62] border border-[#0FA49C]/40 hover:bg-[#1f6d62] hover:text-white hover:border-[#1f6d62] transition-colors"
                          data-testid={`checkout-voucher-chip-${v.id}`}
                          title={v.description || v.label}
                        >
                          <span className="font-mono tracking-widest">{v.code}</span>
                          <span className="font-semibold opacity-80">{v.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="discount-code"
                      type="text"
                      placeholder="Enter discount code"
                      value={discountCode}
                      onChange={(e) => {
                        setDiscountCode(e.target.value);
                        if (promoResult) setPromoResult(null);
                      }}
                      className={`pl-10 bg-gray-50 border-gray-200 focus:border-[#0FA49C] uppercase ${
                        promoResult?.valid ? 'border-green-500 bg-green-50' : 
                        promoResult && !promoResult.valid ? 'border-red-400 bg-red-50' : ''
                      }`}
                      data-testid="checkout-discount-input"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleApplyCode}
                    disabled={!discountCode.trim()}
                    className="px-4 shrink-0"
                    data-testid="apply-discount-btn"
                  >
                    Apply
                  </Button>
                </div>
                {promoResult?.valid && (
                  <p className="text-xs text-green-600 flex items-center gap-1" data-testid="discount-success-msg">
                    <CheckCircle2 className="w-3 h-3" />
                    {promoResult.source === 'voucher'
                      ? `${promoResult.label || 'Voucher'} applied — will be marked used when you place the order.`
                      : 'Code applied — your box is free!'}
                  </p>
                )}
                {promoResult && !promoResult.valid && (
                  <p className="text-xs text-red-500" data-testid="discount-error-msg">
                    Invalid discount code. Please try again.
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-[#0FA49C] text-white hover:bg-[#0d8f88] rounded-xl py-6 text-lg font-bold btn-primary"
                disabled={isSubmitting}
                data-testid="submit-order-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : promoResult?.valid ? (
                  <>
                    Claim Free Box
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                ) : (
                  <>
                    Continue to Checkout
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-4"
          >
            <div className="w-20 h-20 rounded-full bg-[#0FA49C]/20 flex items-center justify-center mx-auto mb-4">
              <Package className="w-10 h-10 text-[#0FA49C]" />
            </div>
            <h3 className="text-2xl font-bold text-[#222] mb-2">
              {redirectUrl ? 'Box Claimed!' : 'Order Saved!'}
            </h3>
            <p className="text-[#666] mb-6">
              {redirectUrl 
                ? 'Your free sample box has been claimed. Click below to continue.'
                : 'Your sample box selection has been saved. Click below to complete your purchase on our store.'
              }
            </p>
            
            <Button
              className="w-full bg-[#0FA49C] text-white hover:bg-[#0d8f88] rounded-xl py-6 text-lg font-bold btn-primary"
              onClick={handleRedirect}
              data-testid="redirect-btn"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              {redirectUrl ? 'Continue' : 'Go to Ultimate Pet Foods'}
            </Button>

            <p className="text-xs text-gray-400 mt-4">
              {redirectUrl 
                ? 'You\'ll be redirected to claim your free box'
                : 'You\'ll be redirected to ultimatepetfoods.co.uk'
              }
            </p>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
};
